import React, {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  useState,
  useEffect,
  KeyboardEvent,
} from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactComponent as PersonIcon } from "../../assets/homePage/profilee-icon.svg";
import { ReactComponent as ShoppingCartIcon } from "../../assets/homePage/cart-icon.svg";
import { ProductListingProps } from "../../types/ProductList";
import { fetchProductListingData } from "../../api/ProductListAPI";
import { productEndPoints } from "../../constants/endpoints";
import { ReactComponent as SearchIcon } from "../../assets/homePage/search-icon.svg";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { ReactComponent as PermIdentityOutlinedIcon } from "../../assets/homePage/drop-profile.svg";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import "./NavBar.css";
import { AuthApiService } from "../../service/authService";
import { Skeleton } from "@mui/lab";
import { mapProducts } from "../../utils/productListMapping";
import supportInfoIcon from "../../assets/customerNavbarIcons/supportInfoicon.svg";
import ledgerIcon from "../../assets/customerNavbarIcons/ledger.svg";

//search product functions
const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(1),
  fontSize: "14px",
  color: "#000",
}));

const Search = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#f1f1f1",
  borderRadius: theme.shape.borderRadius,
  padding: "4px 8px",
  flexGrow: 1,
  maxWidth: "100%",
  border: "1px solid #ccc",
  transition: "border-color 0.3s",
  position: "relative",

  "&:hover": {
    borderColor: "#000",
  },
}));

const Dropdown = styled(List)(({ theme }) => ({
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  width: "100%",
  backgroundColor: "#fff",
  border: "1px solid #ccc",
  borderRadius: theme.shape.borderRadius,
  zIndex: 1000,
  maxHeight: "200px",
  overflowY: "auto",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
}));

interface NavbarProps {
  children?: ReactNode;
  logo?: string;
}
const Navbar = ({ children, logo }: NavbarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState<ProductListingProps[]>([]);
  const [userName] = useState<string>(localStorage.getItem("Name") || "");
  const [filteredProducts, setFilteredProducts] = useState<
    ProductListingProps[]
  >([]);
  const isMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [orgId, setOrgId] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchProductListingData(
          productEndPoints.getProducts
        );
        const mappedData = mapProducts(data.products);

        setProducts(mappedData);
      } catch (error) {
        console.error("Error fetching product data:", error);
      }
    };
    const Organization = localStorage.getItem("orgId") ?? "";
    setOrgId(Organization);
    fetchData();
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      const matches = products.filter((product) =>
        product.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredProducts(matches);
      setHighlightedIndex(-1);
    } else {
      setFilteredProducts([]);
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(`products/product/${productId}`);
    setSearchQuery("");
    setFilteredProducts([]);
  };
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (filteredProducts.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, filteredProducts.length - 1)
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (event.key === "Enter" && highlightedIndex >= 0) {
      event.preventDefault();
      handleProductClick(filteredProducts[highlightedIndex].id);
    }
  };
  const handleSearchBlur = () => {
    setFilteredProducts([]);
  };
  const handleNavigateToLedger = () => {
    navigate("ledgers");
    handleMenuClose();
  };
  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNavigateToOrders = () => {
    navigate("my-orders");
    handleMenuClose();
  };
  const handleNavigateToProfile = () => {
    navigate("customer-profile");
    handleMenuClose();
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCart = () => {
    navigate("cart");
  };

  const handleLogout = () => {
    setAnchorEl(null);
    AuthApiService.logout();
    navigate(`/${orgId}`, {
      replace: true,
    });
  };

  const menuItems = [
    {
      to: "home",
      icon: <HomeOutlinedIcon />,
      text: "Home",
    },
    {
      to: "products",
      icon: <InboxOutlinedIcon />,
      text: "Products",
    },
    {
      to: "help-support",
      icon: (
        <img
          src={supportInfoIcon}
          alt="Support Icon"
          width="24px"
          height="24px"
        />
      ),
      text: "Support",
    },
  ];

  const dropdownIconStyle = { width: "16px", height: "16px" };
  const dropDownItems = [
    {
      icon: <PermIdentityOutlinedIcon style={dropdownIconStyle} />,
      text: "Profile",
      onClick: handleNavigateToProfile,
    },
    {
      icon: (
        <img
          src={ledgerIcon}
          alt="Ledger Icon"
          width={dropdownIconStyle.width}
          height={dropdownIconStyle.height}
        />
      ),
      text: "Ledger",
      onClick: handleNavigateToLedger,
    },
    {
      icon: <ShoppingCartOutlinedIcon style={dropdownIconStyle} />,
      text: "My Orders",
      onClick: handleNavigateToOrders,
    },
    {
      icon: <LogoutOutlinedIcon style={dropdownIconStyle} />,
      text: "Log Out",
      onClick: handleLogout,
    },
  ];

  // Ensure the JSX is properly returned here

  return (
    <div>
      <AppBar
        position="static"
        color="default"
        sx={{ fontFamily: "Poppins, sans-serif" }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            paddingX: "25px !important",
          }}
        >
          <Typography
            variant="h6"
            noWrap
            sx={{
              display: "flex",
              alignItems: "center",
              marginRight: "20px",
            }}
          >
            <NavLink to="home" style={{ textDecoration: "none" }}>
              {!logo ? (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: "46px",
                    width: "46px",
                  }}
                />
              ) : (
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    height: "50px",
                    cursor: "pointer",
                  }}
                />
              )}
            </NavLink>
          </Typography>

          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <Search>
              <SearchInput
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                onKeyDown={handleSearchKeyDown}
              />
              {filteredProducts.length > 0 && (
                <Dropdown
                  onMouseLeave={() => {
                    setSearchQuery(""); // Clear the search bar
                    setFilteredProducts([]); // Clear the suggestions
                  }}
                >
                  {filteredProducts.map((product, index) => (
                    <ListItem
                      key={product.id}
                      disablePadding
                      onMouseDown={() => handleProductClick(product.id)} // Use onMouseDown instead of onClick
                      sx={{
                        backgroundColor:
                          index === highlightedIndex
                            ? "#f0f0f0"
                            : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <ListItemButton>{product.name}</ListItemButton>
                    </ListItem>
                  ))}
                </Dropdown>
              )}
              <SearchIcon />
            </Search>
          </Box>

          <IconButton
            color="inherit"
            disableRipple
            onClick={handleProfileMenuOpen}
            sx={{ marginLeft: 1.5 }}
          >
            <PersonIcon />
            <Typography
              variant="body2"
              sx={{ marginLeft: "5px", fontWeight: 500 }}
            >
              {userName}

              <strong>
                <KeyboardArrowDownOutlinedIcon
                  sx={{ fontSize: "inherit", position: "relative", top: "3px" }}
                />
              </strong>
            </Typography>
          </IconButton>

          <IconButton color="inherit" disableRipple onClick={handleCart}>
            <ShoppingCartIcon />
            {cartItems && cartItems.length > 0 && (
              <Typography className="cartCount">{cartItems.length}</Typography>
            )}
            <Typography
              variant="body2"
              sx={{ marginLeft: "5px", fontWeight: 500 }}
            >
              Cart
            </Typography>
          </IconButton>
        </Toolbar>

        <Box
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            padding: "10px",
            display: "flex",
            gap: "60px",
            paddingLeft: "20px",
          }}
        >
          {menuItems.map((item) => (
            <NavLink
              to={item.to}
              style={{ textDecoration: "none", color: "#fff" }}
              key={item.to}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                {item.icon}
                <Typography>{item.text}</Typography>
              </Box>
            </NavLink>
          ))}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          sx={{
            transform: "translateX(6px)",
            // transform: "translate(6px,5px)",
            ".css-1toxriw-MuiList-root-MuiMenu-list": {
              outline: 0,
              fontFamily: "Poppins, sans-serif",
              borderRadius: "10px",
              border: "1px solid #64748B",
            },
            ".MuiPaper-root": {
              boxShadow: "none",
            },
            ".MuiMenuItem-root": {
              padding: "8px 15px",
              marginX: "6px",

              "&:hover": {
                borderRadius: "4px",
              },
            },
          }}
        >
          {dropDownItems.map((item, idx) => (
            <>
              <MenuItem onClick={item.onClick} key={item.text}>
                {item.icon}
                <Typography sx={{ fontSize: "14px", paddingLeft: "8px" }}>
                  {item.text}
                </Typography>
              </MenuItem>
              {idx < dropDownItems.length - 1 && (
                <Divider sx={{ margin: "4px 0" }} />
              )}
            </>
          ))}
        </Menu>
      </AppBar>
      {children}
    </div>
  );
};

export default Navbar;
